// Router configuration with public and protected routes (tenant only; owner removed)
import { Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import Inside from '@layout/Inside'; // Direct Inside layout
import Outside from '@layout/Outside';
import Signin from '@/pages/outside/Signin';
import Signup from '@/pages/outside/Signup';
import ForgotPassword from '@/pages/outside/ForgotPassword';
import Otp from '@/pages/outside/Otp';
import VerifyEmail from '@/pages/outside/VerifyEmail';
import ResetPassword from '@/pages/outside/ResetPassword';
import AcceptInvite from '@/pages/outside/AcceptInvite';
import AcceptClientInvitation from '@/pages/outside/AcceptClientInvitation';
import Onboarding from '@/pages/outside/Onboarding';
import NotFound from '@/pages/outside/NotFound';
import NotAuthorized from '@/pages/outside/NotAuthorized';
import RequireAuth from '@routes/guards/RequireAuth';
import RequireAbility from '@routes/guards/RequireAbility';
import { useAuthStore } from '@stores/auth.store';
import { getDashboardRoute } from '@utils/routing';
import { tenantRoutes } from '@routes/tenant.routes';
import RouteErrorPage from '@routes/RouteErrorPage';
import Cube3DLoader from '@/components/ui/Cube3DLoader';

const activeTenantRoutes = tenantRoutes;

import { Spinner } from '@/pages/style';

/** Attach a neutral error UI so React Router does not show its default peach/orange screen. */
const withRouteErrorElement = (routes) =>
    routes.map((route) => ({
        ...route,
        errorElement: route.errorElement ?? <RouteErrorPage />,
    }));

/** No full-page spinner on route transitions; keep previous UI until the next view is ready. */
const SuspenseWrapper = ({ children }) => <Suspense fallback={null}>{children}</Suspense>;

const ConditionalLayout = ({ children }) => {
    const user = useAuthStore((state) => state.user);
    const isRefreshing = useAuthStore((state) => state.isRefreshing);

    if (isRefreshing) {
        return <Cube3DLoader overlay size={48} />;
    }

    return user ? <Inside>{children}</Inside> : <Outside>{children}</Outside>;
};

// Root redirect: sign-in when not logged in, dashboard when logged in
const RootRedirect = () => {
    const user = useAuthStore((state) => state.user);
    const role = useAuthStore((state) => state.role);
    const isRefreshing = useAuthStore((state) => state.isRefreshing);

    if (isRefreshing) {
        return <Cube3DLoader overlay size={48} />;
    }

    if (!user) {
        return <Navigate to="/sign-in" replace />;
    }
    const dashboardRoute = getDashboardRoute(role || user?.role);
    return <Navigate to={dashboardRoute} replace />;
};

// Build routes from tenant routes only
const buildDynamicRoutes = () => {
    const dashboardRoute = activeTenantRoutes.find((r) => r.path === '/dashboard');
    const tenantDashboardRouteFull = activeTenantRoutes.find((r) => r.path === '/tenant/dashboard');

    const publicRoutes = [
        {
            path: '/',
            element: <RootRedirect />,
        },
        {
            path: '/dashboard',
            element: (
                <RequireAuth>
                    <Inside>
                        <SuspenseWrapper>
                            <RouteResolver route={dashboardRoute} />
                        </SuspenseWrapper>
                    </Inside>
                </RequireAuth>
            ),
        },
        {
            path: '/tenant/dashboard',
            element: (
                <RequireAuth>
                    <Inside>
                        <SuspenseWrapper>
                            {tenantDashboardRouteFull ? (
                                <RouteResolver route={tenantDashboardRouteFull} />
                            ) : (
                                <NotFound />
                            )}
                        </SuspenseWrapper>
                    </Inside>
                </RequireAuth>
            ),
        },
        {
            path: '/sign-in',
            element: (
                <Outside>
                    <Signin />
                </Outside>
            ),
        },
        {
            path: '/sign-up',
            element: (
                <Outside>
                    <Signup />
                </Outside>
            ),
        },
        {
            path: '/forgot-password',
            element: (
                <Outside>
                    <ForgotPassword />
                </Outside>
            ),
        },
        {
            path: '/otp',
            element: (
                <Outside>
                    <Otp />
                </Outside>
            ),
        },
        {
            path: '/verify-email',
            element: (
                <Outside>
                    <VerifyEmail />
                </Outside>
            ),
        },
        {
            path: '/onboarding',
            element: (
                <Outside>
                    <Onboarding />
                </Outside>
            ),
        },
        {
            path: '/reset-password',
            element: (
                <Outside>
                    <ResetPassword />
                </Outside>
            ),
        },
        {
            path: '/accept-invite',
            element: (
                <Outside>
                    <AcceptInvite />
                </Outside>
            ),
        },
        {
            path: '/accept-client-invitation',
            element: (
                <Outside>
                    <AcceptClientInvitation />
                </Outside>
            ),
        },
        {
            path: '/not-authorized',
            element: (
                <ConditionalLayout>
                    <NotAuthorized />
                </ConditionalLayout>
            ),
        },
        {
            path: '*',
            element: (
                <ConditionalLayout>
                    <NotFound />
                </ConditionalLayout>
            ),
        },
    ];

    // All other tenant paths (roles, permissions, chat, settings, etc.)
    const explicitPaths = ['/dashboard', '/tenant/dashboard'];
    const dynamicRoutes = activeTenantRoutes
        .filter((r) => r.path && !explicitPaths.includes(r.path))
        .map((route) => ({
            path: route.path,
            element: (
                <RequireAuth>
                    <Inside>
                        <SuspenseWrapper>
                            <RouteResolver route={route} />
                        </SuspenseWrapper>
                    </Inside>
                </RequireAuth>
            ),
        }));

    return [...publicRoutes, ...dynamicRoutes];
};

// Renders the component for the given route (tenant only)
const RouteResolver = ({ route }) => {
    if (!route) {
        return <NotFound />;
    }

    const Component = route.component;

    if (route.requireAbility) {
        return (
            <RequireAbility can={route.requireAbility} fallbackElement={<NotFound />}>
                <Component />
            </RequireAbility>
        );
    }

    if (route.requiredPermission) {
        const permissionChecks = Array.isArray(route.requiredPermission)
            ? route.requiredPermission
            : [route.requiredPermission];
        return (
            <RequireAbility can={permissionChecks} fallbackElement={<NotFound />}>
                <Component />
            </RequireAbility>
        );
    }

    return <Component />;
};

const router = createBrowserRouter(withRouteErrorElement(buildDynamicRoutes()));

const RoutesRoot = () => {
    return <RouterProvider router={router} />;
};

export default RoutesRoot;
